'use client';

import { motion } from 'framer-motion';
import { stegaClean } from '@sanity/client/stega';
import { cn } from '@/lib/utils';
import type { ITopBanner } from '@/sanity/lib/definitions';
import { useBannerCountdown } from '@/hooks/useBannerCountdown';
import { getCountdownLabels } from '@/lib/bannerCountdown';
import { FloatingPumpkin, rem, type PumpkinBox } from '@/components/global/seasonal/SeasonalLayers';
import {
  SeasonalBannerCountdown,
  getSeasonalCopy,
} from '@/components/global/seasonal/SeasonalCountdown';
import { BannerPill, splitCopy, useBannerCopy } from '@/components/global/seasonal/SeasonalBannerParts';

/**
 * Top banner — "Halloween 2026" design. Rendered by
 * components/global/TopBanner.tsx when the document's Design field is
 * "halloween".
 *
 * Built from Figma 13041:6363 "Halloween header · névoa + abóboras"
 * (1920x64) and 13047:25231 "Halloween header · mobile" (430x113): the
 * Seedream graveyard strip at 28% under a gradient that darkens the centre,
 * the lit pumpkin at the frames' positions (floating, as in the designer's
 * handoff), and every text, the countdown and the pill as real HTML at the
 * Figma positions. The desktop frame places its three groups absolutely
 * (label at x 165, centre group at the viewport centre, pill ending at
 * x 1679), so the desktop tree does the same in rem; the phone frame is a
 * 12px-padded column: title line, copy line, countdown + pill.
 *
 * Copy: `preText` is the label ("Halloween Special", last word in italic).
 * The [[...]] part of `text` is the offer detail — after the dot in the
 * label on desktop, the serif title line on phones — and the plain part is
 * the centre copy / the line under the title. `mobileText` replaces `text`
 * on phones when the editor filled it in (the Figma phone frame words the
 * offer differently).
 *
 * Sizes: Figma px / 8 (1rem = 8px at the design width, app/globals.scss;
 * on phones at 435px).
 */

interface TopBannerHalloweenProps {
  data: ITopBanner;
  language?: string;
}

const ASSETS = '/seasonal/halloween/header';

interface PumpkinLayer {
  box: PumpkinBox;
  /** Float phase; the two stacked copies of a pumpkin share it so they move as one. */
  index: number;
  spin?: boolean;
  className?: string;
}

/**
 * 13041:6385-6387 + 13047:25312/25313 — the lit pumpkin (e00d3) at
 * (54,14) 53x58, (1759,8) 64x70 and (1837,26) 40x44. The right two are
 * drawn twice at a 1px offset in Figma (which is what makes them brighter
 * than the left one), so they are drawn twice here; they are anchored to
 * the right edge to keep their corner at any width. The big one turns in
 * 3D as well (the handoff spins one pumpkin per piece).
 */
const DESKTOP_PUMPKINS: PumpkinLayer[] = [
  { box: { x: 54, y: 14, width: 53, height: 58 }, index: 0 },
  { box: { x: 1920 - 1759 - 64, y: 8, width: 64, height: 70, fromRight: true }, index: 1, spin: true },
  { box: { x: 1920 - 1759 - 64, y: 7, width: 64, height: 70, fromRight: true }, index: 1, spin: true },
  { box: { x: 1920 - 1837 - 40, y: 26, width: 40, height: 44, fromRight: true }, index: 2 },
  { box: { x: 1920 - 1837 - 40, y: 25, width: 40, height: 44, fromRight: true }, index: 2 },
];

/**
 * 13047:25318 / 13047:25317 — the phone frame's pumpkins: (330,5) 64x70 and
 * a 40x44 one turned -10.9deg and blurred 1.85px whose rotated box sits at
 * (392,-5) 47.6x50.77, i.e. the element itself at (395.8,-1.6).
 */
const MOBILE_PUMPKINS: PumpkinLayer[] = [
  { box: { x: 430 - 330 - 64, y: 5, width: 64, height: 70, fromRight: true }, index: 1, spin: true },
  {
    box: { x: 430 - 395.8 - 40, y: -1.6, width: 40, height: 44, fromRight: true },
    index: 2,
    className: 'rotate-[-10.9deg] blur-[0.23125rem]',
  },
];

/**
 * "Halloween Special" (13041:6393): Blacker Display 22 with the last word
 * in italic. Editors override the label per language (Pre Text); the last
 * word of whatever they write is the italic one.
 */
function splitLastWord(label: string): [string, string] {
  const trimmed = label.trim();
  const at = trimmed.lastIndexOf(' ');
  return at === -1 ? [trimmed, ''] : [trimmed.slice(0, at + 1), trimmed.slice(at + 1)];
}

const DEFAULT_PRE_TEXT = 'Halloween Special';

/** 13041:6365 / 13047:25233 "escurecer centro" */
const CENTRE_SHADE =
  'linear-gradient(90deg, rgba(9,18,15,0) 0%, rgba(9,18,15,0.85) 25%, rgba(9,18,15,0.85) 75%, rgba(9,18,15,0) 100%)';

export default function TopBannerHalloween({ data, language }: TopBannerHalloweenProps) {
  const { preText, segments, mobileSegments, ctaLabel, finalLink } = useBannerCopy(data, language);
  const [labelHead, labelTail] = splitLastWord(stegaClean(preText) || DEFAULT_PRE_TEXT);
  const ctaFallbackLabel = stegaClean(preText) || DEFAULT_PRE_TEXT;
  const desktop = splitCopy(segments);
  const mobile = splitCopy(mobileSegments);

  // null while the countdown window is closed -> no countdown at all.
  const timeLeft = useBannerCountdown(data);
  const cleanLanguage = stegaClean(language) || undefined;
  const countdownLabels = getCountdownLabels(cleanLanguage);
  const copy = getSeasonalCopy(cleanLanguage);

  return (
    <motion.div
      initial={{ opacity: 0, y: 'calc(-100% - 1.5rem)' }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      className="relative w-full overflow-clip bg-[#09120f] lg:h-[8rem]"
      data-seasonal-banner="halloween"
    >
      {/* decorative layers — the graveyard strip, the centre shade and the pumpkins */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        {/* 13041:6364 / 13047:25323 — the Seedream strip at 28%: the full
            1920x64 on desktop; on phones the same 1920-wide box, 113 tall,
            shifted to x -373 and cropped by object-cover. */}
        {/* eslint-disable-next-line @next/next/no-img-element -- decorative plate */}
        <img
          src={`${ASSETS}/strip.webp`}
          alt=""
          className="absolute left-[-46.625rem] top-0 h-full w-[240rem] max-w-none object-cover opacity-[0.28] lg:left-0 lg:w-full"
        />
        <div className="absolute inset-0" style={{ backgroundImage: CENTRE_SHADE }} />
        {DESKTOP_PUMPKINS.map((pumpkin, i) => (
          <FloatingPumpkin
            key={`d${i}`}
            src={`${ASSETS}/pumpkin.webp`}
            box={pumpkin.box}
            index={pumpkin.index}
            spin={pumpkin.spin}
            className="hidden lg:block"
          />
        ))}
        {MOBILE_PUMPKINS.map((pumpkin, i) => (
          <FloatingPumpkin
            key={`m${i}`}
            src={`${ASSETS}/pumpkin.webp`}
            box={pumpkin.box}
            index={pumpkin.index}
            spin={pumpkin.spin}
            className={cn('lg:hidden', pumpkin.className)}
          />
        ))}
      </div>
      {/* end decorative layers */}

      {/* Phone frame 13047:25234 "Conteúdo": 12px padding, 10px gaps —
          title + line, then countdown and pill at the row's two ends. */}
      <div className="relative z-[1] flex flex-col gap-[1.25rem] p-[1.5rem] lg:hidden">
        <div className="flex flex-col items-start">
          {/* 13047:25241 — Blacker Display 20/24, -0.6 */}
          {mobile.highlighted && (
            <p className="m-0 font-blacker text-[2.5rem] font-normal leading-[3rem] tracking-[-0.075rem] text-white">
              {mobile.highlighted}
            </p>
          )}
          {/* 13047:25311 — Geist Medium 14/20, -0.5 */}
          {mobile.plain && (
            <p className="m-0 font-geist text-[1.75rem] font-medium leading-[2.5rem] tracking-[-0.0625rem] text-white">
              {mobile.plain}
            </p>
          )}
        </div>
        <div className="flex items-center justify-between gap-[1.5rem]">
          {timeLeft && (
            <SeasonalBannerCountdown
              timeLeft={timeLeft}
              labels={countdownLabels}
              scale={0.873}
              boxHeight={35.24}
            />
          )}
          {finalLink && (
            <BannerPill
              href={finalLink}
              label={ctaLabel}
              fallbackLabel={ctaFallbackLabel}
              className="ml-auto pl-[2.75rem] pr-[2.25rem]"
            />
          )}
        </div>
      </div>

      {/* Desktop frame 13041:6363: three groups at their Figma positions. */}
      <div className="absolute inset-0 z-[1] hidden lg:block">
        {/* 13041:6388 — statue pumpkin · "Halloween Special" · dot · offer
            detail, gap 10, at (165, 18.5); the label is cap-trimmed, so the
            group's centre line (y 32.5) runs through the capitals. */}
        <div className="absolute left-[20.625rem] top-[2.3125rem] flex items-center gap-[1.25rem] whitespace-nowrap">
          {/* eslint-disable-next-line @next/next/no-img-element -- decorative */}
          <img
            src={`${ASSETS}/statue-pumpkin.webp`}
            alt=""
            className="h-[3.5rem] w-[3.75rem] shrink-0 object-contain"
          />
          {/* Blacker Display 22 / -0.66: "Halloween " regular + "Special" italic */}
          <span className="font-blacker text-[2.75rem] font-normal leading-none tracking-[-0.0825rem] text-white [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
            {labelHead}
            {labelTail && <span className="font-blacker italic">{labelTail}</span>}
          </span>
          {desktop.highlighted && (
            <>
              {/* 13041:6412 — 5px #f9ea66 dot */}
              <span className="h-[0.625rem] w-[0.625rem] shrink-0 rounded-full bg-[#f9ea66]" />
              {/* 13041:6408 — Geist Medium 16/22.5, -0.5, white 60% */}
              <span className="font-geist text-[2rem] font-medium leading-[2.8125rem] tracking-[-0.0625rem] text-white/60">
                {desktop.highlighted}
              </span>
            </>
          )}
        </div>

        {/* 13041:6366 — copy · diamond · "Ends in" + countdown, gap 20,
            centred on the viewport, top 14.5 (36 tall with the countdown). */}
        <div className="absolute left-1/2 top-[1.8125rem] flex -translate-x-1/2 items-center gap-[2.5rem] whitespace-nowrap">
          {/* 13041:6367 — Geist Medium 16/22.5, -0.5 */}
          {desktop.plain && (
            <p className="m-0 font-geist text-[2rem] font-medium leading-[2.8125rem] tracking-[-0.0625rem] text-white">
              {desktop.plain}
            </p>
          )}

          {timeLeft && (
            <>
              {/* 13041:6368 — the yellow diamond: an 18.42 box holding a
                  square rotated 45deg (white 18% fill, 0.5 hairline, 3.048
                  padding, 3.048 blur) around a 5.93 #f9ea66 square. The
                  hairline is an inset shadow (Chrome would snap a 0.5px
                  border to 1px and the diamond would grow by 1.4px). */}
              <span
                aria-hidden
                className="flex shrink-0 items-center justify-center"
                style={{ width: rem(18.421), height: rem(18.421) }}
              >
                <span
                  className="flex -rotate-45 items-center bg-white/[0.18] backdrop-blur-[3px]"
                  style={{ padding: rem(3.048 + 0.5), boxShadow: `inset 0 0 0 ${rem(0.5)} rgba(255,255,255,0.1)` }}
                >
                  <span className="block bg-[#f9ea66]" style={{ width: rem(5.93), height: rem(5.93) }} />
                </span>
              </span>

              {/* 13041:6370 — "Ends in" Geist SemiBold 14/19.5 #b8c8c2 uppercase + the boxes, gap 8 */}
              <div className="flex items-center gap-[1rem]">
                <span className="font-geist text-[1.75rem] font-semibold uppercase leading-[2.4375rem] text-[#b8c8c2]">
                  {copy.endsIn}
                </span>
                <SeasonalBannerCountdown timeLeft={timeLeft} labels={countdownLabels} />
              </div>
            </>
          )}
        </div>

        {/* 13041:6397 "Link" — x 1510..1679 at 1920, top 14; anchored to its
            right edge so a longer label grows leftwards. */}
        {finalLink && (
          <div className="absolute right-[30.125rem] top-[1.75rem]">
            <BannerPill
              href={finalLink}
              label={ctaLabel}
              fallbackLabel={ctaFallbackLabel}
              className="pl-[2.75rem] pr-[2.25rem]"
            />
          </div>
        )}
      </div>
    </motion.div>
  );
}
